/**
 * Validation utilities for forms
 * Provides consistent validation across the application
 */

// Field length limits
export const LIMITS = {
  TITLE_MAX: 200,
  DESCRIPTION_MAX: 500,
  LOCATION_MAX: 300,
  NOTES_MAX: 2000,
  FLIGHT_NUMBER_MAX: 20,
  TERMINAL_MAX: 50,
  BOOKING_REFERENCE_MAX: 50,
  CHECK_IN_INFO_MAX: 500,
  LUGGAGE_ALLOWANCE_MAX: 200,
  AMOUNT_MAX: 999999.99,
  AMOUNT_MIN: 0.01,
};

// Valid categories
export const VALID_EVENT_CATEGORIES = ['flight', 'accommodation', 'transit', 'activity'];
export const VALID_EXPENSE_CATEGORIES = ['transport', 'accommodation', 'visas', 'activities', 'food_drink', 'miscellaneous'];
export const VALID_CURRENCIES = ['EUR', 'GBP', 'USD'];

/**
 * Validate a single field and return error message or null
 */
export function validateField(field, value, options = {}) {
  const { required = false, maxLength, minLength, min, max, pattern, patternMessage } = options;
  
  // Required check
  if (required && (value === undefined || value === null || value === '')) {
    return 'This field is required';
  }
  
  // If not required and empty, skip other validations
  if (value === undefined || value === null || value === '') {
    return null;
  }
  
  // String validations
  if (typeof value === 'string') {
    if (maxLength && value.length > maxLength) {
      return `Maximum ${maxLength} characters allowed`;
    }
    if (minLength && value.length < minLength) {
      return `Minimum ${minLength} characters required`;
    }
    if (pattern && !pattern.test(value)) {
      return patternMessage || 'Invalid format';
    }
  }
  
  // Number validations
  if (typeof value === 'number') {
    if (min !== undefined && value < min) {
      return `Minimum value is ${min}`;
    }
    if (max !== undefined && value > max) {
      return `Maximum value is ${max}`;
    }
  }
  
  return null;
}

/**
 * Validate event form data
 * @returns {{ isValid: boolean, errors: Record<string, string> }}
 */
export function validateEventForm(data) {
  const errors = {};
  
  // Title - required, max 200 chars
  const titleError = validateField('title', data.title?.trim(), {
    required: true,
    maxLength: LIMITS.TITLE_MAX,
  });
  if (titleError) errors.title = titleError;
  
  // Category - required, must be valid
  if (!data.category) {
    errors.category = 'Please select a category';
  } else if (!VALID_EVENT_CATEGORIES.includes(data.category)) {
    errors.category = 'Invalid category selected';
  }
  
  // Start date - required
  if (!data.startDate) {
    errors.startDate = 'Start date is required';
  } else if (!isValidDateFormat(data.startDate)) {
    errors.startDate = 'Invalid date format';
  }
  
  // Start time - required
  if (!data.startTime) {
    errors.startTime = 'Start time is required';
  } else if (!isValidTimeFormat(data.startTime)) {
    errors.startTime = 'Invalid time format';
  }
  
  // End date - required
  if (!data.endDate) {
    errors.endDate = 'End date is required';
  } else if (!isValidDateFormat(data.endDate)) {
    errors.endDate = 'Invalid date format';
  }
  
  // End time - required
  if (!data.endTime) {
    errors.endTime = 'End time is required';
  } else if (!isValidTimeFormat(data.endTime)) {
    errors.endTime = 'Invalid time format';
  }
  
  // Date/time comparison - end must be >= start (considering different timezones for flights)
  if (data.startDate && data.endDate && data.startTime && data.endTime) {
    const startDateTime = new Date(`${data.startDate}T${data.startTime}`);
    const endDateTime = new Date(`${data.endDate}T${data.endTime}`);
    
    // For same timezone events, end must be after start
    // For flights/transit, we allow some flexibility due to timezone differences
    const needsSeparateTimezones = data.category === 'flight' || data.category === 'transit';
    
    if (!needsSeparateTimezones && endDateTime < startDateTime) {
      errors.endDate = 'End date/time must be after start date/time';
    }
  }
  
  // Timezone - required
  if (!data.departure_timezone) {
    errors.departure_timezone = 'Timezone is required';
  }
  
  // Arrival timezone for flights/transit
  const needsSeparateTimezones = data.category === 'flight' || data.category === 'transit';
  if (needsSeparateTimezones && !data.arrival_timezone) {
    errors.arrival_timezone = 'Arrival timezone is required';
  }
  
  // Optional fields with length limits
  const locationError = validateField('location', data.location?.trim(), {
    maxLength: LIMITS.LOCATION_MAX,
  });
  if (locationError) errors.location = locationError;
  
  const notesError = validateField('notes', data.notes?.trim(), {
    maxLength: LIMITS.NOTES_MAX,
  });
  if (notesError) errors.notes = notesError;
  
  // Travel detail fields (only validate if flight/transit)
  if (needsSeparateTimezones) {
    const flightNumberError = validateField('flight_number', data.flight_number?.trim(), {
      maxLength: LIMITS.FLIGHT_NUMBER_MAX,
    });
    if (flightNumberError) errors.flight_number = flightNumberError;
    
    const terminalError = validateField('terminal', data.terminal?.trim(), {
      maxLength: LIMITS.TERMINAL_MAX,
    });
    if (terminalError) errors.terminal = terminalError;
    
    const bookingRefError = validateField('booking_reference', data.booking_reference?.trim(), {
      maxLength: LIMITS.BOOKING_REFERENCE_MAX,
    });
    if (bookingRefError) errors.booking_reference = bookingRefError;
    
    const checkInError = validateField('check_in_info', data.check_in_info?.trim(), {
      maxLength: LIMITS.CHECK_IN_INFO_MAX,
    });
    if (checkInError) errors.check_in_info = checkInError;
    
    const luggageError = validateField('luggage_allowance', data.luggage_allowance?.trim(), {
      maxLength: LIMITS.LUGGAGE_ALLOWANCE_MAX,
    });
    if (luggageError) errors.luggage_allowance = luggageError;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate expense form data
 * @returns {{ isValid: boolean, errors: Record<string, string> }}
 */
export function validateExpenseForm(data, entryType = 'expense') {
  const errors = {};
  
  // Description - required for standalone expenses, optional for combined
  if (entryType === 'expense') {
    const descriptionError = validateField('description', data.description?.trim(), {
      required: true,
      maxLength: LIMITS.DESCRIPTION_MAX,
    });
    if (descriptionError) errors.description = descriptionError;
  }
  
  // Category - required
  if (!data.category) {
    errors.category = 'Please select a category';
  } else if (!VALID_EXPENSE_CATEGORIES.includes(data.category)) {
    errors.category = 'Invalid category selected';
  }
  
  // Amount - required, positive number
  const amount = parseFloat(data.amount);
  if (data.amount === '' || data.amount === undefined || data.amount === null) {
    errors.amount = 'Amount is required';
  } else if (isNaN(amount)) {
    errors.amount = 'Amount must be a valid number';
  } else if (amount < LIMITS.AMOUNT_MIN) {
    errors.amount = `Minimum amount is ${LIMITS.AMOUNT_MIN}`;
  } else if (amount > LIMITS.AMOUNT_MAX) {
    errors.amount = `Maximum amount is ${LIMITS.AMOUNT_MAX.toLocaleString()}`;
  }
  
  // Currency - required, must be valid
  if (!data.currency) {
    errors.currency = 'Currency is required';
  } else if (!VALID_CURRENCIES.includes(data.currency)) {
    errors.currency = 'Invalid currency selected';
  }
  
  // Date - required for standalone expenses
  if (entryType === 'expense') {
    if (!data.expense_date) {
      errors.expense_date = 'Date is required';
    } else if (!isValidDateFormat(data.expense_date)) {
      errors.expense_date = 'Invalid date format';
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate budget form data
 */
export function validateBudgetForm(data) {
  const errors = {};
  
  // Category - required
  if (!data.category) {
    errors.category = 'Please select a category';
  } else if (!VALID_EXPENSE_CATEGORIES.includes(data.category)) {
    errors.category = 'Invalid category selected';
  }
  
  // Amount - required, positive
  const amount = parseFloat(data.amount);
  if (data.amount === '' || data.amount === undefined || data.amount === null) {
    errors.amount = 'Budget amount is required';
  } else if (isNaN(amount)) {
    errors.amount = 'Amount must be a valid number';
  } else if (amount < 0) {
    errors.amount = 'Budget amount cannot be negative';
  } else if (amount > LIMITS.AMOUNT_MAX) {
    errors.amount = `Maximum amount is ${LIMITS.AMOUNT_MAX.toLocaleString()}`;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Check if string is a valid date format (YYYY-MM-DD)
 */
export function isValidDateFormat(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return false;
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) return false;
  
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
}

/**
 * Check if string is a valid time format (HH:MM)
 */
export function isValidTimeFormat(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return false;
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
  return timeRegex.test(timeStr);
}

/**
 * Sanitize string input - trim and limit length
 */
export function sanitizeString(value, maxLength = LIMITS.DESCRIPTION_MAX) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length > maxLength ? trimmed.substring(0, maxLength) : trimmed;
}

/**
 * Sanitize and parse amount
 */
export function sanitizeAmount(value) {
  const amount = parseFloat(value);
  if (isNaN(amount)) return null;
  // Round to 2 decimal places
  return Math.round(amount * 100) / 100;
}
